import { useState } from 'react';
import { BoutonCorriger, _inp } from '../../components/ui/Inputs.jsx';
import { ChampUnite } from '../../components/ui/ChampUnite.jsx';
import { metierUnites } from '../../constants/trades';
import { DEVISE, fmt } from '../../lib/currency';
import { detectMetier } from '../../lib/discounts';
import { tr, trU } from '../../lib/i18n';
import { uid } from '../../lib/utils';
import { C } from '../../theme/palette';

/** Le nom d'un champ, en petites capitales comme les autres fiches. */
const titreChamp = {
  display: 'block',
  fontSize: 9,
  fontWeight: 700,
  color: C.muted,
  marginBottom: 10,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

/** Un nombre tapé au téléphone : la virgule vaut le point. */
const nombre = (v) => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/**
 * « ✍️ Article qui n'est pas dans mon catalogue » : la petite fiche d'un
 * article tapé à la main (désignation, description, unité, quantité, prix),
 * avec le total de la ligne et, au choix, son enregistrement au catalogue
 * pour la prochaine fois. La même fiche sert dans l'offre en cours de saisie
 * et dans une offre ou une facture déjà faite.
 *
 * @param {object} props propriétés
 * @param {object} props.soc société (unités du métier)
 * @param {boolean} props.estMag pièce du magasin
 * @param {object[]} [props.catalogue] articles de l'entreprise
 * @param {Function} [props.setCatalogue] pour garder l'article au catalogue
 * @param {string} [props.nomDepart] désignation déjà tapée (recherche sans résultat)
 * @param {string} [props.metier] corps de métier où ranger l'article gardé
 * @param {string} props.libelleAjout texte du bouton
 * @param {(ligne: object) => void} props.onAjout reçoit la ligne prête
 * @param {() => void} props.onAnnuler referme la fiche
 * @returns {import('react').ReactElement} la fiche
 */
export function FicheArticleLibre({
  soc,
  estMag,
  catalogue,
  setCatalogue,
  nomDepart = '',
  metier,
  libelleAjout,
  onAjout,
  onAnnuler,
}) {
  const unites = metierUnites(soc, estMag);
  const [f, setF] = useState({
    d: nomDepart,
    sd: '',
    u: estMag ? unites[0] || 'pièce' : 'forfait',
    q: '1',
    p: '',
    garder: false,
    erreur: '',
  });
  const maj = (k) => (e) =>
    setF((x) => ({ ...x, [k]: e && e.target ? e.target.value : e, erreur: '' }));
  const peutGarder = !estMag && !!setCatalogue;
  const total = nombre(f.q) * nombre(f.p);

  const ajoute = () => {
    const d = f.d.trim();
    if (!d) {
      setF((x) => ({ ...x, erreur: tr('Écris d’abord la désignation.') }));
      return;
    }
    const sd = f.sd.trim();
    const p = nombre(f.p);
    if (f.garder && peutGarder) {
      const nid =
        'A' +
        ((catalogue || []).reduce((m, x) => Math.max(m, +String(x.id).replace(/\D/g, '') || 0), 0) +
          1);
      setCatalogue((pc) => [
        ...pc,
        { id: nid, metier: metier || detectMetier(d) || 'À classer', nom: d, u: f.u, p, sd },
      ]);
    }
    // La ligne reste libre (sans référence), comme une ligne tapée à la main :
    // la fiche gardée au catalogue servira aux prochaines pièces.
    onAjout({ id: uid(), d, sd, u: f.u, q: nombre(f.q) || 1, p, rem: 0, ref: null, taxe: true });
  };

  return (
    <div
      role="group"
      aria-label={tr('Article qui n’est pas dans mon catalogue')}
      style={{
        border: '1.5px solid ' + C.green,
        borderRadius: 13,
        padding: 12,
        background: C.white,
        marginTop: 8,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 900, color: C.ink, marginBottom: 8 }}>
        {tr('✍️ Article qui n’est pas dans mon catalogue')}
      </div>
      <label style={titreChamp}>
        {trU(estMag ? 'Désignation' : 'Désignation — de quoi il s’agit')}
        <input
          value={f.d}
          onChange={maj('d')}
          placeholder={estMag ? tr('ex. Pièce sur commande') : tr('ex. Plafond, salle de bain')}
          style={{ ..._inp, marginTop: 3, textTransform: 'none' }}
        />
      </label>
      <label style={{ ...titreChamp, marginBottom: 0 }}>
        {trU(estMag ? 'Description (facultatif)' : 'Description — ce qu’on fait (facultatif)')}
        {/* Assez haute pour une vraie description (tablette comprise), et
            elle grandit encore avec le texte. */}
        <textarea
          rows={5}
          value={f.sd}
          onChange={maj('sd')}
          onInput={(e) => {
            const zone = e.currentTarget;
            zone.style.height = 'auto';
            zone.style.height = Math.max(zone.scrollHeight, 120) + 'px';
          }}
          placeholder={
            estMag
              ? tr('ex. Marque, référence fournisseur')
              : tr('ex. Préparation du plafond, masticage, ponçage, prêt à peindre')
          }
          style={{
            ..._inp,
            marginTop: 3,
            minHeight: 120,
            resize: 'vertical',
            fontFamily: 'inherit',
            textTransform: 'none',
            lineHeight: 1.45,
          }}
        />
      </label>
      <BoutonCorriger value={f.sd} onChange={maj('sd')} />
      <div style={{ marginTop: 10 }}>
        <ChampUnite value={f.u} onChange={maj('u')} unites={unites} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ ...titreChamp, flex: 1 }}>
          {trU('Quantité')} ({f.u})
          <input
            value={f.q}
            inputMode="decimal"
            onChange={maj('q')}
            style={{ ..._inp, marginTop: 3, textAlign: 'center', fontWeight: 800 }}
          />
        </label>
        <label style={{ ...titreChamp, flex: 1 }}>
          {trU('Prix')} ({DEVISE})
          <input
            value={f.p}
            inputMode="decimal"
            placeholder="0.00"
            onChange={maj('p')}
            style={{ ..._inp, marginTop: 3, textAlign: 'center', fontWeight: 800 }}
          />
        </label>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: C.navyXL,
          borderRadius: 9,
          padding: '9px 12px',
        }}
      >
        <span style={{ fontSize: 10, color: C.grey, fontWeight: 700 }}>
          {tr('Total de la ligne')}
        </span>
        <span style={{ fontSize: 13.5, fontWeight: 900, color: C.navy }}>
          {DEVISE} {fmt(total)}
        </span>
      </div>
      {peutGarder && (
        <button
          type="button"
          role="checkbox"
          aria-checked={f.garder}
          onClick={() => setF((x) => ({ ...x, garder: !x.garder }))}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 9,
            border: 'none',
            background: 'none',
            padding: 0,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 18,
              height: 18,
              borderRadius: 5,
              border: '1.5px solid ' + (f.garder ? C.green : C.border),
              background: f.garder ? C.green : '#fff',
              color: '#fff',
              fontSize: 12,
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 18px',
            }}
          >
            {f.garder ? '✓' : ''}
          </span>
          <span style={{ fontSize: 10, color: C.ink, fontWeight: 700 }}>
            {tr('Enregistrer aussi dans mon catalogue (réutilisable)')}
          </span>
        </button>
      )}
      {f.erreur && (
        <div role="alert" style={{ fontSize: 10, color: C.red, fontWeight: 700, marginTop: 8 }}>
          {f.erreur}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          type="button"
          onClick={onAnnuler}
          style={{
            border: '1px solid ' + C.border,
            background: C.white,
            color: C.grey,
            borderRadius: 10,
            padding: '11px 12px',
            fontSize: 11,
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          {tr('Annuler')}
        </button>
        <button
          type="button"
          onClick={ajoute}
          style={{
            flex: 1,
            border: 'none',
            background: C.green,
            color: '#fff',
            borderRadius: 10,
            padding: 11,
            fontSize: 12,
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          {libelleAjout}
        </button>
      </div>
    </div>
  );
}
